import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Modal from "Components/UI/Modal";
import Form from "Components/UI/Form/Form";
import Field from "Components/UI/Field";
import Button from "Components/UI/Button";
import { IconName } from "Components/UI";
import useDebounce from "Hooks/useDebounce";
import { IDiscipline } from "Data/interfaces/Obra/IObraData";
import { getDisciplineDisplayIcon } from "Utils/ObraUtils";
import DisciplineIconPicker from "../DisciplineIconPicker";
import schema from "./discipline.schema";
import {
  getDisciplineDisplayAcronym,
  getDisciplineDisplayName,
} from "../../disciplines.helper";
import { BtnContainer, Content, Divider, FieldContainer, Row } from "./styles";

export interface IDisciplineFormValues {
  acronym: string;
  name: string;
  icon: IconName;
}

interface IDisciplineModal {
  visible: boolean;
  editDiscipline: IDiscipline | null;
  disciplines: IDiscipline[];
  loading?: boolean;
  onSubmit: (values: IDisciplineFormValues) => void;
  onCancel: () => void;
}

const DisciplineModal: FC<IDisciplineModal> = ({
  visible,
  editDiscipline,
  disciplines,
  loading,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation('editObra');

  const [isSubmit, setIsSubmit] = useState(false);
  const [acronym, setAcronym] = useState(
    editDiscipline ? getDisciplineDisplayAcronym(editDiscipline) : ''
  );
  const [name, setName] = useState(
    editDiscipline ? getDisciplineDisplayName(editDiscipline) : ''
  );
  const [icon, setIcon] = useState<IconName>(
    editDiscipline ? getDisciplineDisplayIcon(editDiscipline) : 'menuContexto'
  );

  const { debouncedValue: debouncedAcronym } = useDebounce(acronym, 400);
  const { debouncedValue: debouncedName } = useDebounce(name, 400);

  const otherDisciplines = useMemo(() => (
    disciplines.filter((discipline) => (
      discipline.ConstructionSiteDisciplinesId !== editDiscipline?.ConstructionSiteDisciplinesId
    ))
  ), [disciplines, editDiscipline]);

  const acronymError = useMemo(() => {
    if (!debouncedAcronym) return undefined;
    const duplicate = otherDisciplines.some((discipline) => (
      getDisciplineDisplayAcronym(discipline).toLowerCase() === debouncedAcronym.toLowerCase()
    ));
    return duplicate ? t('disciplines.duplicateAcronym') : undefined;
  }, [debouncedAcronym, otherDisciplines, t]);

  const nameError = useMemo(() => {
    if (!debouncedName) return undefined;
    const duplicate = otherDisciplines.some((discipline) => (
      getDisciplineDisplayName(discipline).toLowerCase() === debouncedName.toLowerCase()
    ));
    return duplicate ? t('disciplines.duplicateName') : undefined;
  }, [debouncedName, otherDisciplines, t]);

  const disabled = useMemo(() => {
    if (acronymError || nameError) return true;

    if (!editDiscipline) {
      return !acronym || !name;
    }

    return getDisciplineDisplayAcronym(editDiscipline) === acronym &&
      getDisciplineDisplayName(editDiscipline) === name &&
      getDisciplineDisplayIcon(editDiscipline) === icon;
  }, [acronym, name, icon, editDiscipline, acronymError, nameError]);

  const handleSend = () => {
    setIsSubmit(true);
  };

  const handleSubmit = (values: { acronym: string; name: string }, errors?: string[]) => {
    if (!values || errors || acronymError || nameError) {
      setIsSubmit(false);
      return;
    }

    onSubmit({ acronym: values.acronym, name: values.name, icon });
    setIsSubmit(false);
  };

  return (
    <Modal
      visible={visible}
      width={450}
      newHeader
      title={editDiscipline ? t('disciplines.editModalTitle') : t('disciplines.createModalTitle')}
      onCancel={onCancel}
      footerButtons={(
        <BtnContainer>
          <Button type="text" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {disabled && (
            <Button type="text" disabled>
              {editDiscipline ? t('common.save') : t('disciplines.createSubmit')}
            </Button>
          )}
          {!disabled && (
            <Button type="primary" onClick={handleSend}>
              {loading && (
                <Spin
                  indicator={
                    <LoadingOutlined
                      color="white"
                      style={{ fontSize: 12, color: 'white', marginRight: 6 }}
                    />
                  }
                />
              )}
              {!editDiscipline && (loading ? t('disciplines.creating') : t('disciplines.createSubmit'))}
              {!!editDiscipline && (loading ? t('common.saving') : t('common.save'))}
            </Button>
          )}
        </BtnContainer>
      )}
    >

      <Form
        schema={schema}
        onSubmit={handleSubmit}
        isSubmited={isSubmit}
        initialValues={{ acronym, name }}
      >
        <Content>
          <Row>
            <FieldContainer>
              <span className="fieldLabel">
                {t('disciplines.iconLabel')} <span className="required">*</span>
              </span>
              <DisciplineIconPicker value={icon} onChange={setIcon} />
            </FieldContainer>
          </Row>

          <Row>
            <FieldContainer width="100px">
              <span className="fieldLabel">
                {t('disciplines.acronymLabel')} <span className="required">*</span>
              </span>
              <Field
                name="acronym"
                label=""
                value={acronym}
                onCustomChange={(value: string) => setAcronym(value.toUpperCase())}
                placeholder={t('disciplines.acronymPlaceholder')}
                maxLenght={5}
                height="32px"
                outError={acronymError}
              />
            </FieldContainer>
            <FieldContainer>
              <span className="fieldLabel">
                {t('disciplines.nameLabel')} <span className="required">*</span>
              </span>
              <Field
                name="name"
                label=""
                value={name}
                onCustomChange={setName}
                placeholder={t('disciplines.namePlaceholder')}
                height="32px"
                outError={nameError}
              />
            </FieldContainer>
          </Row>
        </Content>
      </Form>


      <Divider />
    </Modal>
  );
};

export default DisciplineModal;
